/**
 * useTranscribe — send a recorded clip to the API and get text back.
 *
 * Deliberately separate from `useVoiceRecorder`: one owns the microphone, the
 * other owns the network. Apart, each is testable without the other.
 *
 * THE TRANSCRIPT GOES TO THE COMPOSER, NOT TO THE ASSISTANT. Speech
 * recognition is wrong often enough — product names, numbers, Russian/Kyrgyz
 * code-switching — that auto-sending would turn every misrecognition into a
 * wasted turn and a confusing answer. The user reads, fixes, sends.
 *
 * Requests route through `apiClient` rather than a bare `fetch` so they keep
 * the 401 -> refresh -> retry handling and the Idempotency-Key that every
 * mutating endpoint requires (REQ-ADD-§6.3-A).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ensureGuestSession } from "@/features/chat/guest/api";
import { isRegistrationRequired } from "@/features/chat/guest/allowance";
import { ApiError, NetworkError, apiClient } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";

/** Mirrors the backend `TranscribeResponse`. */
export interface TranscribeResult {
  text: string;
  no_speech: boolean;
  detected_language: string | null;
  duration_ms: number;
  provider: string;
  model: string;
  confidence: number | null;
  remaining_seconds_today: number;
}

/** Mirrors the backend `VoiceConfigResponse`. */
export interface VoiceConfig {
  enabled: boolean;
  languages: string[];
  max_duration_seconds: number;
  max_bytes: number;
  accepted_content_types: string[];
  remaining_seconds_today: number;
}

/**
 * What went wrong, in terms the composer can render. Mapped from the
 * server's named codes so the UI never parses prose.
 */
export type TranscribeErrorKind =
  | "permission"
  | "rateLimited"
  | "quotaExhausted"
  | "tooLong"
  | "unsupported"
  | "generic";

export interface TranscribeFailure {
  kind: TranscribeErrorKind;
  /** Seconds to wait, when the server said so. */
  retryAfterSeconds?: number;
}

export const VOICE_CONFIG_PATH = "/chat/voice/config";
export const TRANSCRIBE_PATH = "/chat/transcribe";

/** Server error code -> what the user is shown. */
export function classifyError(code: string | undefined): TranscribeErrorKind {
  switch (code) {
    case "stt.rate_limited":
    case "stt.rate_limited_ip":
      return "rateLimited";
    case "stt.daily_quota_exhausted":
      return "quotaExhausted";
    case "stt.too_large":
      return "tooLong";
    case "stt.disabled":
    case "stt.language_unsupported":
    case "stt.unsupported_media_type":
      return "unsupported";
    default:
      return "generic";
  }
}

/** Guest mode (SCRUM-273): the voice fields of GET /auth/guest/config. */
export interface UseTranscribeGuestOptions {
  /** `voice_enabled`. */
  voiceEnabled: boolean;
  /** `voice_languages` (contract C2) — used until a guest session exists. */
  languages: string[];
  /** `voice_max_duration_seconds` (contract C2). */
  maxDurationSeconds: number;
  /**
   * The server refused the clip with 403 `auth.registration_required` —
   * guest voice was switched off after the config was read (amendment B-7).
   * The caller opens the sign-up gate; the hook hides the mic.
   */
  onSignInRequired?: () => void;
}

export interface UseTranscribeOptions {
  /** Absent for members: the hook is exactly what it was. */
  guest?: UseTranscribeGuestOptions;
}

export interface UseTranscribeResult {
  /** Server-side availability; null until the config call resolves. */
  config: VoiceConfig | null;
  isTranscribing: boolean;
  failure: TranscribeFailure | null;
  /** Upload a clip. Resolves with the result, or null when it failed. */
  transcribe: (
    blob: Blob,
    mimeType: string,
    durationMs: number,
    languageHint: string,
  ) => Promise<TranscribeResult | null>;
  clearFailure: () => void;
}

export function useTranscribe(options: UseTranscribeOptions = {}): UseTranscribeResult {
  const guestMode = options.guest !== undefined;
  const guestVoiceEnabled = options.guest?.voiceEnabled ?? false;
  // Primitives, so a fresh options object every render does not re-run effects.
  const guestLanguages = options.guest?.languages.join(",") ?? "";
  const guestMaxSeconds = options.guest?.maxDurationSeconds ?? 0;
  const memberPresent = useAuthStore((s) => s.accessToken !== null);
  const guestSessionPresent = useAuthStore((s) => s.guestToken !== null);
  // Latest callback without re-creating `transcribe` (B-7).
  const onSignInRequiredRef = useRef(options.guest?.onSignInRequired);
  onSignInRequiredRef.current = options.guest?.onSignInRequired;
  // Constant for members, so a member's config is still fetched exactly once.
  const guestAsMember = guestMode && memberPresent;
  const guestHasSession = guestMode && guestSessionPresent;

  const [config, setConfig] = useState<VoiceConfig | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [failure, setFailure] = useState<TranscribeFailure | null>(null);

  // Availability is fetched, not assumed: an admin turning voice off — or
  // turning one language off — has to make the microphone disappear, not
  // merely make it fail after the user has already spoken.
  useEffect(() => {
    let cancelled = false;
    if (guestMode && !guestAsMember) {
      if (!guestVoiceEnabled) {
        setConfig(null);
        return () => {
          cancelled = true;
        };
      }
      if (!guestHasSession) {
        // No guest token yet (the session starts with the first send or
        // clip), and GET /chat/voice/config would 401: the public guest
        // config answers instead (contract C2). Only `enabled`, `languages`
        // and `max_duration_seconds` are read by the composer.
        setConfig({
          enabled: true,
          languages: guestLanguages === "" ? [] : guestLanguages.split(","),
          max_duration_seconds: guestMaxSeconds,
          max_bytes: 0,
          accepted_content_types: [],
          remaining_seconds_today: 0,
        });
        return () => {
          cancelled = true;
        };
      }
    }
    const request = guestMode
      ? apiClient.get<VoiceConfig>(VOICE_CONFIG_PATH, { allowGuest: true })
      : apiClient.get<VoiceConfig>(VOICE_CONFIG_PATH);
    void request
      .then((value) => {
        if (!cancelled) setConfig(value);
      })
      .catch(() => {
        // Unreachable config means no microphone. Failing closed is right
        // here: showing a button that cannot work is worse than hiding it.
        if (!cancelled) setConfig(null);
      });
    return () => {
      cancelled = true;
    };
  }, [guestMode, guestAsMember, guestHasSession, guestVoiceEnabled, guestLanguages, guestMaxSeconds]);

  const transcribe = useCallback(
    async (
      blob: Blob,
      mimeType: string,
      durationMs: number,
      languageHint: string,
    ): Promise<TranscribeResult | null> => {
      setIsTranscribing(true);
      setFailure(null);
      try {
        // Guest chat: the first clip creates the session. It runs here, AFTER
        // the recording, so the recorder's start (getUserMedia +
        // AudioContext.resume) stayed inside the tap's user activation (iOS).
        if (guestMode && useAuthStore.getState().accessToken === null) {
          await ensureGuestSession();
        }

        const form = new FormData();
        // A fixed part name: the server derives the extension from the
        // validated content type and never trusts a client filename.
        form.append("audio", blob, "recording");
        form.append("language_hint", languageHint);
        form.append("duration_ms", String(Math.round(durationMs)));

        const result = guestMode
          ? await apiClient.post<TranscribeResult, FormData>(TRANSCRIBE_PATH, form, {
              allowGuest: true,
            })
          : await apiClient.post<TranscribeResult, FormData>(TRANSCRIBE_PATH, form);
        setConfig((current) =>
          current === null
            ? current
            : { ...current, remaining_seconds_today: result.remaining_seconds_today },
        );
        return result;
      } catch (error) {
        // B-7: guest voice switched off server-side after the config was
        // read. A sign-in requirement, not an error: hide the mic and let
        // the caller open the sign-up gate.
        if (guestMode && isRegistrationRequired(error)) {
          setConfig((current) => (current === null ? current : { ...current, enabled: false }));
          onSignInRequiredRef.current?.();
          return null;
        }
        if (error instanceof ApiError) {
          setFailure({
            kind: classifyError(error.code),
            retryAfterSeconds: error.retryAfterSeconds,
          });
        } else if (error instanceof NetworkError) {
          setFailure({ kind: "generic" });
        } else {
          setFailure({ kind: "generic" });
        }
        return null;
      } finally {
        setIsTranscribing(false);
      }
    },
    [guestMode],
  );

  const clearFailure = useCallback((): void => setFailure(null), []);

  return { config, isTranscribing, failure, transcribe, clearFailure };
}
